import { Popper } from '@mui/material';
import { useGetWidthCategory } from '@utilities/common_hooks';
import CommonUtilities from '@utilities/common_utilities';
import React, { CSSProperties, useEffect, useState } from 'react';

type FlexType = CSSProperties['flex']; 

interface Props extends React.ComponentPropsWithoutRef<'div'> {
  xs_shrink?: number;
  xs_expand?: number;
  xs_width?: string; // auto | num% | numpx
  sm_shrink?: number;
  sm_expand?: number;
  sm_width?: string; // auto | num% | numpx
  md_shrink?: number;
  md_expand?: number;
  md_width?: string; // auto | num% | numpx
  lg_shrink?: number;
  lg_expand?: number;
  lg_width?: string; // auto | num% | numpx
  direction?: "row" | "column";
  log_it?: boolean;
  tooltip?: React.ReactNode;
}

const common_util = CommonUtilities.getInstance();

export default function FlexBox(props: Props) {
  const { ref, widthCategory } = useGetWidthCategory();
  const [hovering, setHovering] = useState(false);
  const [hovering_tooltip, setHoveringTooltip] = useState(false);
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState(false);
  const [anti_checking, setAntiChecking] = useState(false);
  const [anti_check, setAntiCheck] = useState(false);
  const [display_tooltip, setDisplayTooltip] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (check) {
      setChecking(false);
      if (hovering) {
        setCheck(false);
        if (props.log_it) {
          console.log("DISPLAY TRUE");
        }
        setDisplayTooltip(true);
      }
    }
  }, [check, hovering, props.log_it]);

  useEffect(() => {
    if (hovering && !checking && !display_tooltip) {
      setChecking(true);
      setTimeout(() => {
        setCheck(true);
      }, 1000);
    }
  }, [hovering, checking, display_tooltip]);

  useEffect(() => {
    if (anti_check) {
      // console.log("Anti check");
      setAntiChecking(false);
      if (!hovering && !hovering_tooltip) {
        setAntiCheck(false);
        if (props.log_it) {
          console.log("DISPLAY FALSE");
        }
        setDisplayTooltip(false);
      }
    }
  }, [hovering, hovering_tooltip, anti_check, props.log_it]);

  useEffect(() => {
    if (display_tooltip && !hovering && !hovering_tooltip && !anti_checking) {
      // console.log("Anti checking");
      setAntiChecking(true);
      setTimeout(() => {
        setAntiCheck(true);
      }, 1000);
    }
  }, [display_tooltip, hovering, hovering_tooltip, anti_checking]);

  let use_style: CSSProperties = props.style ? { minHeight: 0, boxSizing: "border-box", ...props.style } : { minHeight: 0, boxSizing: "border-box" };
  let shrink = props.direction ? undefined : 1;
  let expand = props.direction ? undefined : 1;
  let width = props.direction ? undefined : "auto";
  if (props.direction) {
    use_style.display = "flex";
    use_style.flexDirection = props.direction;
  }
  switch (widthCategory) {
    case "lg": 
      if (props.lg_shrink !== undefined) {
        shrink = props.lg_shrink;
      } else if (props.md_shrink !== undefined) {
        shrink = props.md_shrink;
      } else if (props.sm_shrink !== undefined) {
        shrink = props.sm_shrink;
      } else if (props.xs_shrink !== undefined) {
        shrink = props.xs_shrink;
      }
      if (props.lg_expand !== undefined) {
        expand = props.lg_expand;
      } else if (props.md_expand !== undefined) {
        expand = props.md_expand;
      } else if (props.sm_expand !== undefined) {
        expand = props.sm_expand;
      } else if (props.xs_expand !== undefined) {
        expand = props.xs_expand;
      }
      if (props.lg_width !== undefined) {
        width = props.lg_width;
      } else if (props.md_width !== undefined) {
        width = props.md_width;
      } else if (props.sm_width !== undefined) {
        width = props.sm_width;
      } else if (props.xs_width !== undefined) {
        width = props.xs_width;
      }
    break;
    case "md": 
      if (props.md_shrink !== undefined) {
        shrink = props.md_shrink;
      } else if (props.sm_shrink !== undefined) {
        shrink = props.sm_shrink;
      } else if (props.xs_shrink !== undefined) {
        shrink = props.xs_shrink;
      }
      if (props.md_expand !== undefined) {
        expand = props.md_expand;
      } else if (props.sm_expand !== undefined) {
        expand = props.sm_expand;
      } else if (props.xs_expand !== undefined) {
        expand = props.xs_expand;
      }
      if (props.md_width !== undefined) {
        width = props.md_width;
      } else if (props.sm_width !== undefined) {
        width = props.sm_width;
      } else if (props.xs_width !== undefined) {
        width = props.xs_width;
      }
    break;
    case "sm": 
      if (props.sm_shrink !== undefined) {
        shrink = props.sm_shrink;
      } else if (props.xs_shrink !== undefined) {
        shrink = props.xs_shrink;
      }
      if (props.sm_expand !== undefined) {
        expand = props.sm_expand;
      } else if (props.xs_expand !== undefined) {
        expand = props.xs_expand;
      }
      if (props.sm_width !== undefined) {
        width = props.sm_width;
      } else if (props.xs_width !== undefined) {
        width = props.xs_width;
      }
    break;
    case "xs": 
      if (props.xs_shrink !== undefined) {
        shrink = props.xs_shrink;
      }
      if (props.xs_expand !== undefined) {
        expand = props.xs_expand;
      }
      if (props.xs_width !== undefined) {
        width = props.xs_width;
      }
    break;
  }
  if (shrink !== undefined || expand !== undefined || width !== undefined) {
    if (shrink === undefined) {
      shrink = 1;
    }
    if (expand === undefined) {
      expand = 1;
    }
    if (width === undefined) {
      width = "auto";
    }
    const flex_style: FlexType = `${expand} ${shrink} ${width}`;
    use_style.flex = flex_style;
  }
  const pass_along_props = {...props};
  delete pass_along_props.xs_expand;
  delete pass_along_props.xs_shrink;
  delete pass_along_props.xs_width;
  delete pass_along_props.sm_expand;
  delete pass_along_props.sm_shrink;
  delete pass_along_props.sm_width;
  delete pass_along_props.md_expand;
  delete pass_along_props.md_shrink;
  delete pass_along_props.md_width;
  delete pass_along_props.lg_expand;
  delete pass_along_props.lg_shrink;
  delete pass_along_props.lg_width;
  delete pass_along_props.log_it;
  delete pass_along_props.direction;
  delete pass_along_props.style;
  delete pass_along_props.tooltip;

  if (props.tooltip) {
    if (props.log_it) {
      // debugger;
      console.log("Why won't this work?");
      console.log(`display_tooltip: ${display_tooltip}`);
      console.log(`hovering: ${hovering}`);
      console.log(`hovering_tooltip: ${hovering_tooltip}`);
      console.log(`check: ${check}`);
      console.log(`checking: ${checking}`);
      console.log(`anti_checking: ${anti_checking}`);
    }
    const renderExtras = () => {
      if (props.tooltip) {
        return (
          <Popper
            open={ display_tooltip }
            anchorEl={popoverAnchorEl}
            placement="bottom"
            sx={{
              // MUI's Modal/Dialog sets an explicit z-index (theme.zIndex.modal,
              // 1300 by default). Popper has no z-index of its own, so without
              // this it can end up stacked behind an open modal.
              zIndex: (theme) => theme.zIndex.modal + 1,
            }}
            modifiers={[
              {
                // Flips to "top" on its own once there isn't enough room
                // below in the viewport - this is the piece that replaces
                // our old manual height-measuring logic.
                name: 'flip',
                enabled: true,
                options: {
                  fallbackPlacements: ['top'],
                  padding: 8,
                },
              },
              {
                // Nudges the tooltip sideways/keeps it clamped within the
                // viewport if it would otherwise spill past an edge.
                name: 'preventOverflow',
                enabled: true,
                options: {
                  padding: 8,
                },
              },
            ]}
          >
            <div className="padded bordered" style={{ backgroundColor: "black" }}
              onMouseEnter={() => {
                setHoveringTooltip(true);
              }}
              onMouseLeave={() => {
                setHoveringTooltip(false);
              }}
              onClick={props.onClick}>{ props.tooltip }</div>
          </Popper>
        );
      }
      return null;
    }

    delete pass_along_props.onClick;
    return (
      <>
        <div ref={ref} style={use_style} {...pass_along_props}
          onMouseEnter={(event: React.MouseEvent<HTMLDivElement>) => {
            setPopoverAnchorEl(event.currentTarget);
            setHovering(true);
          }}
          onMouseLeave={() => {
            setHovering(false);
          }} onClick={props.onClick}>
          <div>
            { props.children }
          </div>
        </div>
        { renderExtras() }
      </>
    );
  } else {
    return (
      <div ref={ref} style={use_style} {...pass_along_props}>{ props.children }</div>
    );
  }
}